import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ListChecks,
  FileText,
  Megaphone,
  LifeBuoy,
  User,
  Settings,
  LogOut,
  BarChart3,
  DollarSign,
  Users as UsersIcon,
  GraduationCap,
  ClipboardCheck,
  Award,
  Briefcase,
  Route,
  BookOpen,
  FileCheck,
  FileBarChart,
  Bot,
  SlidersHorizontal,
  ClipboardList,
  CalendarClock,
  Monitor,
} from "lucide-react";
import { company } from "../mock";
import { useAuth } from "../context/AuthContext";

const APPLICANT_NAV = [
  { label: "Dashboard", path: "/dashboard", Icon: LayoutDashboard },
  { label: "My Onboarding", path: "/onboarding", Icon: ListChecks },
  { label: "Forms & Documents", path: "/documents", Icon: FileText },
  { label: "My Availability", path: "/availability", Icon: CalendarClock },
  { label: "Equipment Request", path: "/equipment", Icon: Monitor },
  { label: "Training", path: "/training", Icon: GraduationCap },
  { label: "Assessment", path: "/assessment", Icon: ClipboardCheck },
  { label: "Certification", path: "/certification", Icon: Award },
  { label: "My Program", path: "/my-program", Icon: Briefcase },
  { label: "Opportunities", path: "/opportunities", Icon: Briefcase },
  { label: "Client Journey", path: "/client-journey", Icon: Route },
  { label: "Announcements", path: "/announcements", Icon: Megaphone },
  { label: "Support", path: "/support", Icon: LifeBuoy },
  { label: "My Profile", path: "/profile", Icon: User },
  { label: "Settings", path: "/settings", Icon: Settings },
];

const ADMIN_NAV = [
  { label: "Workforce", path: "/admin/workforce", Icon: BarChart3 },
  { label: "Supervisor Hub", path: "/supervisor/hub", Icon: BookOpen },
  { label: "Metrics", path: "/metrics", Icon: FileBarChart },
  { label: "Client Programs", path: "/admin/programs", Icon: Briefcase },
  { label: "Client Companies", path: "/admin/clients", Icon: UsersIcon },
  { label: "People", path: "/users", Icon: UsersIcon },
  { label: "My Applicants", path: "/supervisor/applicants", Icon: ClipboardList },
  { label: "Applicant Data", path: "/admin/applicant-data", Icon: CalendarClock },
  { label: "Document Review", path: "/admin/documents", Icon: FileCheck },
  { label: "Forms Manager", path: "/admin/forms", Icon: ClipboardList },
  { label: "Training Content", path: "/admin/content", Icon: BookOpen },
  { label: "Certificates", path: "/admin/certificates", Icon: Award },
  { label: "Patriot Setup", path: "/admin/patriot", Icon: DollarSign },
  { label: "Payroll", path: "/payroll", Icon: DollarSign },
  { label: "Reports", path: "/admin/reports", Icon: FileBarChart },
  { label: "Audit Log", path: "/admin/audit", Icon: ClipboardCheck },
  { label: "Notifications", path: "/admin/notifications", Icon: Megaphone },
  { label: "Cami Manager", path: "/admin/cami", Icon: Bot },
  { label: "System Settings", path: "/admin/system", Icon: SlidersHorizontal },
  { label: "My Profile", path: "/profile", Icon: User },
  { label: "Settings", path: "/settings", Icon: Settings },
];

const SUPERVISOR_NAV = [
  { label: "Supervisor Hub", path: "/supervisor/hub", Icon: BookOpen },
  { label: "My Applicants", path: "/supervisor/applicants", Icon: UsersIcon },
  { label: "Applicant Data", path: "/admin/applicant-data", Icon: CalendarClock },
  ...APPLICANT_NAV,
];

const CLIENT_NAV = [
  { label: "Dashboard", path: "/client-dashboard", Icon: LayoutDashboard },
  { label: "Programs", path: "/client-programs", Icon: Briefcase },
  { label: "Contractors", path: "/client-contractors", Icon: UsersIcon },
  { label: "Weekly Report", path: "/client-reports", Icon: FileBarChart },
  { label: "My Profile", path: "/profile", Icon: User },
  { label: "Settings", path: "/settings", Icon: Settings },
];

export default function Sidebar({ onNavigate }) {
  const navigate = useNavigate();
  const { user, logout, viewAsApplicant, setViewAsApplicant } = useAuth();
  const role = user?.role || "applicant";
  const isStrictAdmin = role === "administrator" || role === "admin";
  const isSupervisor = role === "supervisor" || role === "recruiter";
  const isClient = role === "client_admin" || role === "client_user";
  const canViewAsApplicant = isStrictAdmin || isSupervisor;

  let items = APPLICANT_NAV;
  if (isClient) items = CLIENT_NAV;
  else if (canViewAsApplicant && viewAsApplicant) items = APPLICANT_NAV;
  else if (isStrictAdmin) items = ADMIN_NAV;
  else if (isSupervisor) items = SUPERVISOR_NAV;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const toggleViewAs = () => {
    const next = !viewAsApplicant;
    setViewAsApplicant(next);
    if (onNavigate) onNavigate();
    navigate(next ? "/dashboard" : (isStrictAdmin ? "/admin/workforce" : "/supervisor/applicants"));
  };

  return (
    <aside className="flex h-full w-64 flex-col border-r border-slate-200 bg-white">
      <div className="flex items-center gap-3 px-5 py-5">
        <img src={company.logo} alt={company.legal} className="h-10 w-10 rounded-lg object-cover ring-1 ring-slate-200" />
        <div className="leading-tight">
          <div className="text-sm font-bold text-slate-900">{company.name}</div>
          <div className="truncate text-[11px] text-slate-400">Powered by Campbell Commu…</div>
        </div>
      </div>

      <div className="px-5 pb-2 pt-3">
        <span className={`text-[11px] font-bold uppercase tracking-wider ${role !== "applicant" ? "text-violet-600" : "text-amber-500"}`}>
          {isClient ? "Client" : (user?.role || "applicant").replace(/^\w/, (c) => c.toUpperCase())}
        </span>
      </div>

      {canViewAsApplicant && viewAsApplicant && (
        <div className="mx-3 mb-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700" data-testid="view-as-applicant-banner">
          Viewing as applicant
        </div>
      )}

      <nav className="flex-1 space-y-1 overflow-y-auto px-3">
        {items.map(({ label, path, Icon }) => (
          <NavLink
            key={path}
            to={path}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-violet-600 text-white shadow-sm shadow-violet-200" : "text-slate-600 hover:bg-slate-100"
              }`
            }
          >
            <Icon className="h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="space-y-1 border-t border-slate-200 p-3">
        {canViewAsApplicant && (
          <button
            onClick={toggleViewAs}
            data-testid="view-as-applicant-toggle"
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-violet-600 transition-colors hover:bg-violet-50"
          >
            <User className="h-4 w-4" />
            {viewAsApplicant ? "Exit applicant view" : "View as Applicant"}
          </button>
        )}
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </button>
      </div>
    </aside>
  );
}
